import {Button, DataStatus, openDialog} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetWorkshopInterestQuery,
  useGetWorkshopInterestUpcomingTasksQuery,
  useListWorkshopInterestActivitiesQuery,
} from '@dms/api/workshop';
import {
  ActivitySidebarSendMessage,
  LogContactDialog,
  transformTaskToWorkshopActivity,
} from '@dms/features/activity-panel';
import {openCreateTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {parseDate} from 'shared/web';

import {ActivityList} from './ActivityList';

export function ActivityTab(props: {interestId: string}) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const [canReadTasks, canCreateTasks] = usePermissions({
    permissionKeys: ['readTask', 'createTask'],
  });

  const {
    data: activities,
    isLoading: isActivitiesLoading,
    isError: isActivitiesError,
  } = useListWorkshopInterestActivitiesQuery(
    {interestId: props.interestId},
    {skip: isNilOrEmpty(props.interestId)}
  );

  const {
    data: upcomingTasks,
    isLoading: isUpcomingTasksLoading,
    isError: isUpcomingTasksError,
  } = useGetWorkshopInterestUpcomingTasksQuery(
    {interestId: props.interestId},
    {skip: isNilOrEmpty(props.interestId) || !canReadTasks}
  );

  const handleNewTaskClick = () => {
    openCreateTaskFormDialog({
      'data-testid': 'workshop-interest-activity-tab-create-task',
      relatedRecordId: props.interestId,
      resourceId: EntityResourceIds.workshopInterest,
    });
  };

  const isAddNewTaskEnabled = canReadTasks && canCreateTasks;
  const isLoading = isActivitiesLoading || (canReadTasks && isUpcomingTasksLoading);
  const isError = isActivitiesError || (canReadTasks && isUpcomingTasksError);

  const upcomingTaskIds = new Set((upcomingTasks ?? []).map((task) => task.id));
  const pastActivities =
    activities?.filter(
      (activity) =>
        parseDate(activity.timestamp) < new Date() &&
        !(
          activity.relatedResourceId === 'TASK' &&
          activity.relatedRecordId &&
          upcomingTaskIds.has(activity.relatedRecordId)
        )
    ) ?? [];

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4} height="100%">
        <HStack spacing={2}>
          <ActivitySidebarSendMessage
            resourceId={EntityResourceIds.workshopInterest}
            recordId={props.interestId}
            customerId={workshopInterest?.customerId ?? ''}
          />
          <Button
            leftIcon="content/add_circle"
            title={i18n.t('entity.task.actions.createTask')}
            variant="link"
            size="small"
            isDisabled={!isAddNewTaskEnabled}
            onClick={handleNewTaskClick}
          />
          <Button
            leftIcon="communication/phone"
            title={i18n.t('entity.activityPanel.activity.logCall')}
            variant="link"
            size="small"
            onClick={() =>
              openDialog(<LogContactDialog workshopInterestId={props.interestId} />, {
                id: 'LOG_CONTACT_DIALOG',
                title: i18n.t('entity.activityPanel.logContact.createLog'),
                scrollBehavior: 'outside',
              })
            }
          />
        </HStack>

        <ActivityList
          showEmpty
          label={i18n.t('entity.activityPanel.activity.nextSteps')}
          items={(upcomingTasks ?? []).map((task) =>
            transformTaskToWorkshopActivity(task, props.interestId)
          )}
          interestId={props.interestId}
        />

        <Show when={isNotNilOrEmpty(pastActivities)}>
          <ActivityList
            label={i18n.t('entity.activityPanel.activity.pastActivities')}
            items={pastActivities}
            interestId={props.interestId}
          />
        </Show>
      </VStack>
    </DataStatus>
  );
}
